import { createContext, useContext, useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router'
import { useAuth, useGymId } from '@structural-climbing/auth'
import { FullPageSpinner } from '@structural-climbing/ui'
import type { PlanTier } from '../lib/tiers'
import {
  DEFAULT_GRADE_CONFIG,
  enabledDisciplines,
  type Discipline,
  type GymDisciplineMode,
  type GymGradeConfig,
} from '@structural-climbing/validation'
import type { WorkingHours } from '../features/settings/types'

export type GymStatus = 'trial' | 'active' | 'suspended' | 'cancelled'

export interface GymContextValue {
  gymId: string
  slug: string
  name: string
  /** The logo the owner uploaded in Settings, already on the gym-info payload. */
  logoUrl?: string | null
  primaryColor: string | null
  secondaryColor: string | null
  timezone: string | null
  workingHours: WorkingHours | null
  mapUrl: string | null
  mapFileType: 'pdf' | 'image' | null
  status: GymStatus
  planTier: PlanTier
  trialEndsAt: string | null
  gradeConfig: GymGradeConfig
  /** The disciplines this gym has; everything else is hidden. */
  disciplines: Discipline[]
  paymentsEnabled: boolean
  /** Whether a super admin has switched the Products page on for this gym. */
  productsEnabled: boolean
}

const GymContext = createContext<GymContextValue | null>(null)

export function useGymContext(): GymContextValue {
  const ctx = useContext(GymContext)
  if (!ctx) throw new Error('useGymContext must be used inside GymContextProvider')
  return ctx
}

/**
 * The gym's timezone, or null outside a gym (the browser's zone is used then).
 * Does not throw, so a modal can read it without every page that opens it
 * having to provide the whole context.
 */
export function useGymTimezone(): string | null {
  return useContext(GymContext)?.timezone ?? null
}

interface GymInfoResponse {
  id: string
  name: string
  slug: string
  image_url?: string | null
  primary_color: string | null
  secondary_color: string | null
  timezone: string | null
  working_hours: WorkingHours | null
  map_url: string | null
  map_file_type: 'pdf' | 'image' | null
  payments_enabled: boolean
}

interface GymStandingResponse {
  status: GymStatus
  plan_tier: PlanTier
  trial_ends_at: string | null
  grade_config?: GymGradeConfig
  disciplines?: GymDisciplineMode
  products_enabled?: boolean
}

type LoadState = 'loading' | 'unauthorized' | 'ready'

interface GymContextProviderProps {
  children: React.ReactNode
}

export function GymContextProvider({ children }: GymContextProviderProps) {
  const { slug } = useParams<{ slug: string }>()
  const jwtGymId = useGymId()
  const { getAccessTokenSilently, isAuthenticated } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [state, setState] = useState<LoadState>('loading')
  const [gymValue, setGymValue] = useState<GymContextValue | null>(null)

  useEffect(() => {
    if (!slug || !jwtGymId) return
    setState('loading')
    let cancelled = false

    async function load() {
      try {
        const infoRes = await fetch(`${apiUrl}/gym-info/by-slug/${slug}`)
        const data = infoRes.ok ? ((await infoRes.json()) as GymInfoResponse) : null
        if (cancelled) return
        if (!data || data.id !== jwtGymId) {
          setState('unauthorized')
          return
        }

        // Fetch the gym's standing for the banner. Fail open to active so a
        // transient error never falsely warns; it's a GET, so a suspended gym
        // can still read it.
        let standing: GymStandingResponse = {
          status: 'active',
          // Fail open to the full plan: a transient error must not strip a
          // paying gym of features it has.
          plan_tier: 'everything',
          trial_ends_at: null,
        }
        try {
          const token = await getAccessTokenSilently()
          const res = await fetch(`${apiUrl}/my-gym-standing`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (res.ok) standing = (await res.json()) as GymStandingResponse
        } catch {
          // keep the active default
        }
        if (cancelled) return

        setGymValue({
          gymId: data.id,
          slug: data.slug,
          name: data.name,
          logoUrl: data.image_url ?? null,
          primaryColor: data.primary_color,
          secondaryColor: data.secondary_color,
          timezone: data.timezone,
          workingHours: data.working_hours ?? null,
          mapUrl: data.map_url,
          mapFileType: data.map_file_type,
          status: standing.status,
          planTier: standing.plan_tier ?? 'everything',
          trialEndsAt: standing.trial_ends_at,
          gradeConfig: standing.grade_config ?? DEFAULT_GRADE_CONFIG,
          // Fails open to both, like the standing itself: a transient error
          // must not hide half a gym's routes.
          disciplines: enabledDisciplines(standing.disciplines),
          // Defaults false so a cached gym-info payload from before this
          // field existed reads as payments-off rather than undefined.
          paymentsEnabled: data.payments_enabled === true,
          // Fails closed, unlike the plan tier: the default standing above has
          // no products_enabled, and offering a page the Worker refuses is
          // worse than hiding one for a single load.
          productsEnabled: standing.products_enabled === true,
        })
        setState('ready')
      } catch {
        if (!cancelled) setState('unauthorized')
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [slug, jwtGymId, apiUrl, getAccessTokenSilently])

  // Signed out (or the session ran out): sign in first. "Access denied" was
  // wrong for someone who simply opened a gym link before logging in.
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!jwtGymId) return <Navigate to="/unauthorized" replace />
  if (state === 'loading') return <FullPageSpinner />
  if (state === 'unauthorized' || !gymValue) return <Navigate to="/unauthorized" replace />

  return <GymContext.Provider value={gymValue}>{children}</GymContext.Provider>
}
